import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ProfileView } from '../../components/Profile/ProfileView/ProfileView';
import { ProfileTopNav } from '../../components/Profile/ProfileTopNav';
import { ProfileEditor } from '../../components/Profile';
import { PremiumModal, CancelSubscriptionModal, ManageSubscriptionModal } from '../../components/Premium';
import { ProfileSetupData, ProfileEditorData } from '../../components/Profile/types';
import { useAuthContext } from '../../providers';
import { logger } from '../../utils/logger';
import { config } from '../../config/env';
import { profileService } from '../../services/Profile/profileService';
import { referralService } from '../../services/Profile/referralService';
import { billingService } from '../../services/Billing/billingService';
import { messagingService } from '../../services/Messaging/messagingService';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  PageContent,
  PageLoading,
  LoadingSpinner,
  LoadingText,
  PageError,
  ErrorMessage,
  ErrorAction,
  SuccessNotification
} from '../../styles/Profile/ProfilePages.styles';
import {
  DrawerBackdrop,
  DrawerContainer,
  DrawerHeader,
  DrawerTitle,
  DrawerCloseButton,
  DrawerContent
} from '../../styles/Profile/ProfileEditor.styles';

export const ProfileViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId?: string }>();
  const { user, loading, isAuthenticated, checkAuth } = useAuthContext();
  const { refreshAfterBillingChange } = useSubscriptionContext();
  const [profileData, setProfileData] = useState<ProfileSetupData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editorData, setEditorData] = useState<ProfileEditorData | null>(null);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check if viewing own profile
  const isOwnProfile = !userId || userId === user?.id;

  // Redirect if not authenticated (skip in mock credentials mode)
  useEffect(() => {
    // In mock credentials mode, allow access even without authentication
    if (config.dev.mockCredentials) {
      return;
    }

    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const fetchProfileData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const isViewingOtherProfile = !!userId && userId !== user?.id;
      const editorData = await profileService.getProfile(isViewingOtherProfile ? userId : undefined);
      setProfileData(convertEditorDataToProfileData(editorData));
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
      setError('Failed to load profile data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // In mock credentials mode, always fetch profile data
    // In normal mode, only fetch if authenticated
    if (config.dev.mockCredentials || isAuthenticated) {
      fetchProfileData();
    }
  }, [isAuthenticated, userId]);

  // Returning from Stripe Checkout: the role/premium flag was just updated
  // server-side by the webhook, but this browser's session was minted
  // before that happened — re-check auth so the new role is picked up
  // without requiring a manual re-login.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutResult = params.get('checkout');
    if (!checkoutResult) return;

    if (checkoutResult === 'success') {
      // Two different things have to catch up, and they are not the same
      // thing: checkAuth refreshes this browser's session so the JWT role
      // claim is current, and refreshAfterBillingChange re-reads the
      // subscription — polling, because Stripe redirects the browser back
      // before its own webhook has necessarily written the new tier. Without
      // the poll, landing here a second too early left the whole app showing
      // Free until something else happened to refetch.
      Promise.all([checkAuth(), refreshAfterBillingChange()]).then(() => {
        setSuccessMessage('Welcome to Premium! Your subscription is now active.');
        setTimeout(() => setSuccessMessage(null), 5000);
      });
    }

    navigate(window.location.pathname, { replace: true });
  }, [checkAuth, navigate, refreshAfterBillingChange]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  // Convert ProfileSetupData to ProfileEditorData
  const convertToEditorData = (data: ProfileSetupData): ProfileEditorData => {
    return {
      basicInfo: {
        ...data.basicInfo,
        location: {
          ...data.basicInfo.location,
          coordinates: data.basicInfo.location.coordinates || {
            lat: 40.7128, // Default coordinates, should come from API
            lng: -74.0060
          }
        }
      },
      photos: data.photos.map(photo => ({
        ...photo,
        validationStatus: photo.isValidated ? 'approved' : 'pending'
      })),
      bio: data.bio,
      interests: data.interests,
      preferences: data.preferences,
      lastUpdated: new Date(),
      version: 1
    };
  };

  // Convert ProfileEditorData back to ProfileSetupData
  const convertEditorDataToProfileData = (editorData: ProfileEditorData): ProfileSetupData => {
    return {
      basicInfo: {
        ...editorData.basicInfo,
        location: {
          ...editorData.basicInfo.location,
          // Remove coordinates if they're not part of ProfileSetupData structure
          coordinates: editorData.basicInfo.location.coordinates
        }
      },
      photos: editorData.photos.map(photo => ({
        ...photo,
        isValidated: photo.validationStatus === 'approved' || photo.isValidated || false
      })),
      bio: editorData.bio,
      interests: editorData.interests,
      preferences: editorData.preferences,
      currentStep: profileData?.currentStep || 5,
      isComplete: profileData?.isComplete || true,
      verificationStatus: profileData?.verificationStatus || 'verified',
      // Preserve persona data from previous profile data
      primaryPersona: profileData?.primaryPersona,
      personaTraits: profileData?.personaTraits,
      personaConfidence: profileData?.personaConfidence,
      // Badges are server-derived and were being dropped here, which is why
      // the pill in ProfileView never rendered for anyone: the check was
      // reading a field this conversion had already thrown away.
      badges: editorData.badges ?? profileData?.badges
    };
  };

  const handleEdit = () => {
    if (profileData) {
      const editorData = convertToEditorData(profileData);
      setEditorData(editorData);
      setIsDrawerOpen(true);
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleSaveProfile = async (data: ProfileEditorData) => {
    // Store previous state for potential rollback
    const previousProfileData = profileData;

    try {
      // Optimistically update UI immediately before backend call
      const updatedProfileData = convertEditorDataToProfileData(data);
      setProfileData(updatedProfileData);

      await profileService.updateProfile(user?.id || '', data);

      // The editor STAYS OPEN. Saving is a checkpoint, not an exit: closing on
      // every save turned "fix my bio, then my photos" into two trips through
      // the drawer, and made the button feel like it had discarded something.
      // The toast is the confirmation instead.
      setSuccessMessage('Profile saved');
      window.setTimeout(() => setSuccessMessage(null), 3000);

      logger.log('Profile saved:', data);
    } catch (error) {
      console.error('Failed to save profile:', error);

      // Rollback to previous state on error
      if (previousProfileData) {
        setProfileData(previousProfileData);
      }

      // The drawer is already open, and the editor keeps whatever was typed.
      // This used to reopen it and reset editorData to the last saved state,
      // which threw away the very edits that had just failed to save — the
      // worst possible moment to discard someone's work.
      //
      // Rethrown so the editor can show its own failure message.
      throw error;
    }
  };

  const handleCancelEdit = () => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel? Any unsaved changes will be lost.'
    );
    if (confirmed) {
      setIsDrawerOpen(false);
    }
  };

  const handlePreview = () => {
    // Navigate to preview page or show preview in a modal
    navigate('/profile/preview');
  };

  // Shares an invite to Plenty of Hearts carrying this user's referral code,
  // not the current URL. window.location.href here is /profile/view/:userId —
  // a route behind auth, so a friend opening it met a login wall, and nothing
  // recorded who sent them.
  const handleShare = async () => {
    const shareData = await referralService.buildShareData(profileData?.basicInfo.firstName);

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error: any) {
        // AbortError is the user dismissing the sheet — not a failure, and
        // falling through to the clipboard would be confusing.
        if (error?.name === 'AbortError') return;
        logger.log('[SHARE] Native share unavailable, copying instead:', error);
      }
    }

    try {
      await navigator.clipboard.writeText(shareData.url);
      setSuccessMessage('Invite link copied to clipboard!');
    } catch {
      setSuccessMessage(shareData.url);
    }
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleMessage = async () => {
    // Message the person whose profile this is: open (or reuse) a conversation
    // with them and jump straight into it. Regular (non-bet) messaging is a
    // premium feature, so a PREMIUM_REQUIRED response opens the upgrade modal.
    if (!userId || !user?.id || isOwnProfile) return;
    try {
      const conversation = await messagingService.createConversation([user.id, userId]);
      navigate('/messaging', { state: { conversationId: conversation.id } });
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === 'PREMIUM_REQUIRED') {
        setIsPremiumModalOpen(true);
      } else {
        logger.error('Failed to open conversation:', err);
        setError('Could not open the conversation. Please try again.');
        setTimeout(() => setError(null), 4000);
      }
    }
  };

  const handleLike = () => {
    // TODO: Implement like functionality
    logger.log('Liked profile');
  };

  const handlePremium = () => {
    setIsPremiumModalOpen(true);
  };

  const handleCancelSubscription = () => {
    // Closes the manager if that is where the request came from, so two
    // full-screen modals are never stacked on top of each other.
    setIsManageModalOpen(false);
    setIsCancelModalOpen(true);
  };

  const handleManageSubscription = () => {
    setIsManageModalOpen(true);
  };

  const handleConfirmCancellation = async (reason: string, feedback: string) => {
    try {
      // Cancel in place rather than redirecting to Stripe's hosted portal.
      // Bouncing someone off-site to finish cancelling is hostile, and it also
      // meant we never learned whether they actually went through with it.
      const { accessUntil } = await billingService.cancelSubscription(reason, feedback);
      setIsCancelModalOpen(false);
      // The plan just changed; re-read it so the profile menu, the pricing
      // modal and every gate reflect the cancellation without a reload.
      refreshAfterBillingChange();

      const until = accessUntil
        ? new Date(accessUntil).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
        : null;
      setSuccessMessage(
        until
          ? `Subscription cancelled. You keep premium access until ${until}, and you can reactivate any time before then.`
          : 'Subscription cancelled. You keep premium access until the end of your billing period.'
      );
      setTimeout(() => setSuccessMessage(null), 8000);
    } catch (error) {
      logger.error('Failed to cancel subscription:', error);
      setIsCancelModalOpen(false);
      setError('Could not cancel your subscription. Please try again.');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Undoes a pending cancellation. Only reachable while the paid period is
  // still running — once it has ended Stripe cannot revive the subscription,
  // and the menu offers "Resubscribe" instead, which goes through checkout at
  // full price.
  const handleReactivateSubscription = async () => {
    try {
      const { renewsOn } = await billingService.resumeSubscription();
      refreshAfterBillingChange();
      const on = renewsOn
        ? new Date(renewsOn).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
        : null;
      setSuccessMessage(
        on
          ? `Welcome back — your subscription is active again and renews on ${on}.`
          : 'Welcome back — your subscription is active again.'
      );
      setTimeout(() => setSuccessMessage(null), 6000);
    } catch (err: any) {
      logger.error('Failed to reactivate subscription:', err);
      // The common case: the period already ended, so this needs a new
      // checkout rather than a resume. Send them to the pricing modal.
      setIsPremiumModalOpen(true);
    }
  };


  if (loading || isLoading) {
    return (
      <PageContainer>
        <PageLoading>
          <LoadingSpinner />
          <LoadingText>Loading profile...</LoadingText>
        </PageLoading>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageError>
          <ErrorMessage>{error}</ErrorMessage>
          <ErrorAction onClick={fetchProfileData}>
            Try Again
          </ErrorAction>
        </PageError>
      </PageContainer>
    );
  }

  if (!profileData) {
    return (
      <PageContainer>
        <PageError>
          <ErrorMessage>Profile not found</ErrorMessage>
          <ErrorAction onClick={() => navigate('/social-feed')}>
            Go to Feed
          </ErrorAction>
        </PageError>
      </PageContainer>
    );
  }

  return (
    <>
      {successMessage && (
        <SuccessNotification>
          ✅ {successMessage}
        </SuccessNotification>
      )}
      <PageContainer>
        {/* TikTok-style Top Navigation */}
        <ProfileTopNav
          title={isOwnProfile ? 'My Profile' : `${profileData.basicInfo.firstName}'s Profile`}
          isOwnProfile={isOwnProfile}
          onPremium={handlePremium}
          onCancelSubscription={handleCancelSubscription}
          onManageSubscription={handleManageSubscription}
          onReactivateSubscription={handleReactivateSubscription}
          onEdit={isOwnProfile ? handleEdit : undefined}
        />

        <PageContent>
          <ProfileView
            profileData={profileData}
            isOwnProfile={isOwnProfile}
            targetUserId={userId || user?.id}
            onEdit={handleEdit}
            onShare={handleShare}
            onMessage={handleMessage}
            onLike={handleLike}
          />
        </PageContent>
      </PageContainer>

      {/* Slide-in Profile Editor Drawer */}
      {isOwnProfile && (
        <>
          <DrawerBackdrop
            $isOpen={isDrawerOpen}
            onClick={handleCloseDrawer}
          />
          <DrawerContainer $isOpen={isDrawerOpen}>
            <DrawerHeader>
              <DrawerTitle>Edit Profile</DrawerTitle>
              <DrawerCloseButton onClick={handleCloseDrawer}>
                ×
              </DrawerCloseButton>
            </DrawerHeader>
            <DrawerContent>
              {editorData && (
                <ProfileEditor
                  initialData={editorData}
                  onSave={handleSaveProfile}
                  onCancel={handleCancelEdit}
                  isPremium={user?.user_metadata?.is_premium || false}
                  onPremium={handlePremium}
                  userId={user?.id || ''}
                />
              )}
            </DrawerContent>
          </DrawerContainer>
        </>
      )}

      {/* Premium Modal */}
      <PremiumModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
      />

      {/* Manage Subscription — plan, renewal date, upgrade and downgrade */}
      <ManageSubscriptionModal
        isOpen={isManageModalOpen}
        onClose={() => setIsManageModalOpen(false)}
        onCancelSubscription={handleCancelSubscription}
        onReactivateSubscription={handleReactivateSubscription}
        onSubscribe={() => {
          setIsManageModalOpen(false);
          handlePremium();
        }}
      />

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirmCancellation={handleConfirmCancellation}
      />
    </>
  );
};
